import { useSearchParams } from 'react-router-dom'
import { useMemo, useEffect, useState } from 'react'
import { useQuery } from 'urql'
import { graphql } from 'app/generated/gql'
import { MarketingContactTable } from '../shared/MarketingContactTable'
import LeadsGraph from './LeadsGraph'
import SummaryTab from './SummaryTab'
import { convertUrlFiltersToES, PredefinedFilter } from 'app/filter.utils'
import type { ActivityJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import type { LeadsRow } from 'types/analytics'
import { useHandleSearchParams } from 'app/utils'
import { StatsCardType } from 'types/StatsCardType'
import type { MetricsRow } from 'app/services/metrics'

const LEADS_BREAKDOWN_QUERY = graphql(`
  query LeadsBreakdown(
    $dateRange: DateRangeInput!
    $channel: String
    $locationIds: [String!]
    $integrationStatus: String
    $filters: String
    $platform: String
    $showDisqualified: Boolean
  ) {
    leadsBreakdown(
      dateRange: $dateRange
      channel: $channel
      locationIds: $locationIds
      integrationStatus: $integrationStatus
      filters: $filters
      platform: $platform
      showDisqualified: $showDisqualified
    ) {
      byActivityTypes {
        bucket
        leads
      }
      rows {
        bucket
        date
        leads
        leadsByFirstActivityType {
          type
          count
        }
        leadsByStatus {
          status
          count
        }
      }
    }
  }
`)

export default function LeadsSection({
  leadsCount,
  summary,
  compareTo,
  channel,
  fromDate,
  toDate,
  locationIds,
  integrationStatus,
  dontCompare,
  filters,
  platform,
  defaultFilters,
  showDisqualified,
}: {
  leadsCount: number
  summary?: MetricsRow
  compareTo?: MetricsRow
  channel: string
  fromDate: string
  toDate: string
  locationIds: string[]
  integrationStatus?: string
  dontCompare: boolean
  filters?: string
  platform?: string
  defaultFilters?: PredefinedFilter
  showDisqualified: boolean
}) {
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const [filterContactsBy, setFilterContactsBy] = useState(searchParams.get(`details`))
  const campaign = useMemo(() => (filters ? convertUrlFiltersToES(filters) : undefined), [filters])

  // Sync filterContactsBy from URL when details param changes (for deep linking)
  useEffect(() => {
    const details = searchParams.get(`details`)
    if (details && details !== filterContactsBy) setFilterContactsBy(details)
  }, [searchParams])

  const handleCloseModal = () => setFilterContactsBy(null)

  const [result] = useQuery({
    query: LEADS_BREAKDOWN_QUERY,
    variables: {
      dateRange: { fromDate, toDate },
      channel,
      locationIds: locationIds.length ? locationIds : undefined,
      integrationStatus,
      filters,
      platform,
      showDisqualified,
    },
    pause: !fromDate || !toDate,
  })

  const breakdown = result.data?.leadsBreakdown

  const activityJsonMetrics = useMemo(
    () =>
      breakdown?.byActivityTypes?.reduce(
        (acc, curr) => {
          acc[curr.bucket] = curr.leads
          return acc
        },
        {} as Record<NonNullable<ActivityJson[`type`]>, number>,
      ),
    [breakdown],
  )

  const leadStatusMetrics = useMemo(
    () =>
      summary &&
      ({
        [`Closed Lost`]: summary.leadsClosedLost ?? 0,
        [`Needs Action`]: summary.leadsNeedsAction ?? 0,
        [`In Progress`]: summary.leadsInProgress ?? 0,
        [`Scheduled`]: summary.leadsScheduled ?? 0,
        [`Closed Won`]: summary.leadsClosedWon ?? 0,
      } as Record<NonNullable<ContactJson[`leadStatus`]>, number>),
    [summary],
  )

  const leadStatusComparisonMetrics = useMemo(
    () =>
      compareTo &&
      ({
        [`Closed Lost`]: compareTo.leadsClosedLost ?? 0,
        [`Needs Action`]: compareTo.leadsNeedsAction ?? 0,
        [`In Progress`]: compareTo.leadsInProgress ?? 0,
        [`Scheduled`]: compareTo.leadsScheduled ?? 0,
        [`Closed Won`]: compareTo.leadsClosedWon ?? 0,
      } as Record<NonNullable<ContactJson[`leadStatus`]>, number>),
    [compareTo],
  )

  const stats: StatsCardType[] = useMemo(() => {
    if (!leadStatusMetrics) return []
    return [
      {
        label: `Closed Lost`,
        value: `${(leadStatusMetrics[`Closed Lost`] ?? 0).toLocaleString(`en-US`)}`,
        trend:
          !dontCompare && leadStatusComparisonMetrics
            ? {
                previous: leadStatusComparisonMetrics[`Closed Lost`],
                current: leadStatusMetrics[`Closed Lost`],
              }
            : undefined,
        onOpenClick: () => openModal(`Closed Lost`),
        infoTooltip: {
          title: `Closed Lost`,
          message: `All contacts who have a lead status of "Closed Lost" and a became a lead date within the date range. A closed lost lead status describes a contact who is disqualified or could not be scheduled for an appointment. Disqualified leads are only counted when Show Disqualified is turned on.`,
        },
      },
      {
        label: `Needs Action`,
        value: `${(leadStatusMetrics[`Needs Action`] ?? 0).toLocaleString(`en-US`)}`,
        trend:
          !dontCompare && leadStatusComparisonMetrics
            ? {
                previous: leadStatusComparisonMetrics[`Needs Action`],
                current: leadStatusMetrics[`Needs Action`],
              }
            : undefined,
        onOpenClick: () => openModal(`Needs Action`),
        infoTooltip: {
          title: `Needs Action`,
          message: `All contacts who have a lead status of "Needs Action" and a became a lead date within the date range. A needs action describes a contact who requires further action from the office administrative team.`,
        },
      },
      {
        label: `In Progress`,
        value: `${(leadStatusMetrics[`In Progress`] ?? 0).toLocaleString(`en-US`)}`,
        trend:
          !dontCompare && leadStatusComparisonMetrics
            ? {
                previous: leadStatusComparisonMetrics[`In Progress`],
                current: leadStatusMetrics[`In Progress`],
              }
            : undefined,
        onOpenClick: () => openModal(`In Progress`),
        infoTooltip: {
          title: `In Progress`,
          message: `All contacts who have a lead status of "In Progress" and a became a lead date within the date range. An in progress lead status describes a contact where the office administrative team is actively taking action to schedule.`,
        },
      },
      {
        label: `Scheduled`,
        value: `${(leadStatusMetrics[`Scheduled`] ?? 0).toLocaleString(`en-US`)}`,
        trend:
          !dontCompare && leadStatusComparisonMetrics
            ? {
                previous: leadStatusComparisonMetrics[`Scheduled`],
                current: leadStatusMetrics[`Scheduled`],
              }
            : undefined,
        onOpenClick: () => openModal(`Scheduled`),
        infoTooltip: {
          title: `Scheduled`,
          message: `All contacts who have a lead status of "Scheduled" and a became a lead date within the date range. A scheduled lead status describes a contact who has a scheduled first appointment in the future.`,
        },
      },
      {
        label: `Closed Won`,
        value: `${(leadStatusMetrics[`Closed Won`] ?? 0).toLocaleString(`en-US`)}`,
        trend:
          !dontCompare && leadStatusComparisonMetrics
            ? {
                previous: leadStatusComparisonMetrics[`Closed Won`],
                current: leadStatusMetrics[`Closed Won`],
              }
            : undefined,
        onOpenClick: () => openModal(`Closed Won`),
        infoTooltip: {
          title: `Closed Won`,
          message: `All contacts who have a lead status of "Closed Won" and a became a lead date within the date range. A closed won lead status describes a contact who has completed a first appointment.`,
        },
      },
    ]
  }, [leadStatusMetrics, leadStatusComparisonMetrics])

  useEffect(() => {
    let currentParam = searchParams.get(`details`)
    if (filterContactsBy === currentParam) return
    handleSearchParams({
      details: filterContactsBy ?? undefined,
      ...(searchParams.has(`filters`) ? { filters: undefined } : undefined),
    })
  }, [filterContactsBy])

  // Note: "Leads" is NOT included here - funnel "Leads" clicks are handled by MarketingGraph
  const validStatuses = [`Closed Lost`, `Needs Action`, `In Progress`, `Scheduled`, `Closed Won`]

  useEffect(() => {
    if (!filterContactsBy || !validStatuses.includes(filterContactsBy) || !fromDate || !toDate) return
    // Defer showModal to allow Root's modal system to initialize first
    const timeoutId = setTimeout(() => {
      showModal(
        <MarketingContactTable
          title={filterContactsBy}
          channel={channel}
          filterContactsBy={filterContactsBy}
          fromDate={fromDate}
          toDate={toDate}
          locationIds={locationIds}
          showDisqualified={showDisqualified}
          integrationStatus={integrationStatus}
          campaign={campaign}
          platform={platform}
          defaultFilters={defaultFilters}
          onClose={handleCloseModal}
        />,
        {
          hideTitle: true,
          removePadding: true,
          isFullScreen: true,
          onClose: handleCloseModal,
          useRadix: true,
        },
      )
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [filterContactsBy, fromDate, toDate])

  const openModal = (type: string) => {
    setFilterContactsBy(type)
  }

  return (
    <>
      <SummaryTab
        title="Leads"
        hideChartOnMobile
        graph={
          <LeadsGraph
            dayMetrics={breakdown?.rows}
            leadStatusMetrics={leadStatusMetrics}
            activityJsonMetrics={activityJsonMetrics}
            leadsCount={leadsCount}
            isMetricsLoading={false}
            isLeadsLoading={result.fetching}
            showDisqualified={showDisqualified}
          />
        }
        statsCards={stats}
      />
    </>
  )
}
